import React, { FC, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';

import cn from 'classnames';
import { createPortal } from 'react-dom';

import Icon from '@app/components/ui/icons';
import useOnClickOutside from '@app/hooks/use-onclick-outside';

import { TriangleBottomIcon } from '@app/components/ui/icons/icons-list';

import TooltipWrapper from '../tooltip-wrapper';

import {
  dropContainerClassName,
  dropItemWrapperClassName,
  dropListItemClassName,
  dropTriangleClassName,
} from './drop-menu.styles';

import { DropMenuItem, DropMenuPosition } from './drop-menu.type';

export type DropMenuItemColor = 'primary' | 'danger';

type DropMenuProps = {
  onClick: (key: string) => void;
  children: ReactNode;
  position?: DropMenuPosition;
  menu: Array<DropMenuItem>;
  dropTop?: boolean;
  disable?: boolean;
  activeKey?: string;
  vladSpecialProp?: boolean;
  onChangeState?: (state: boolean) => void;
  dropSize?: string | number;
  hintTitle?: string;
  dropHeight?: number;
};

const DEFAULT_DROP_SIZE = 274;
const PADDING_FOR_ICON = 5;

const DROP_DOWN_POSITION: Record<DropMenuPosition, string> = {
  end: 'justify-end',
  start: 'justify-start',
  center: 'justify-center',
};

const MENU_COLORS: Record<DropMenuItemColor, string> = {
  primary: 'text-1color',
  danger: 'text-basic_red',
};

const dataDropMenuIgnore = '[data-drop-menu-ignore]';

const DropMenu: FC<DropMenuProps> = ({
  children,
  menu,
  onChangeState,
  onClick,
  position = 'end',
  dropTop = false,
  dropSize,
  hintTitle,
  disable = false,
  vladSpecialProp = false,
  activeKey,
  dropHeight,
}) => {
  const [isOpen, changeIsOpen] = useState(false);
  const [dropOffset, setDropOffset] = useState(0);

  const [menuPosition, setMenuPosition] = useState<{ top?: number; bottom?: number; left: number }>(
    {
      left: 0,
    },
  );
  const tooltipId = useId();
  const dropMenuRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const dropWrapperRef = useRef<HTMLDivElement>(null);

  const sizeStyle = useMemo(
    () =>
      typeof dropSize === 'string'
        ? { width: dropSize }
        : { width: `${dropSize || DEFAULT_DROP_SIZE}px` },
    [dropSize],
  );

  const dropHeightStyle = useMemo(
    () =>
      dropHeight
        ? {
            height: `${dropHeight}px`,
          }
        : undefined,
    [dropHeight],
  );

  useEffect(() => {
    if (!dropWrapperRef.current || position === 'center') {
      setDropOffset(0);
      return;
    }

    setDropOffset(dropWrapperRef.current.offsetWidth / 2 - PADDING_FOR_ICON);
  }, [position, isOpen]);

  useEffect(() => {
    if (!isOpen || !dropWrapperRef.current) return;

    function updatePosition() {
      if (!dropWrapperRef.current) return;

      const rect = dropWrapperRef.current.getBoundingClientRect();

      const width = typeof dropSize === 'number' ? dropSize : DEFAULT_DROP_SIZE;

      let left = rect.left;

      if (position === 'end') {
        left = rect.right - width;
      }

      if (position === 'center') {
        left = rect.left + rect.width / 2 - width / 2;
      }
      const top = dropTop
        ? rect.top - (dropHeight || dropMenuRef.current?.clientHeight || 0) - 25
        : rect.bottom + 8;
      setMenuPosition({
        top,
        left,
      });
    }

    updatePosition();

    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen, dropTop, position, dropSize, dropHeight]);

  useOnClickOutside([rootRef, dropMenuRef], () => {
    if (isOpen) {
      changeIsOpen(false);
      onChangeState?.(false);
    }
  });

  function handleToggleDropMenu(event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();

    const elem = event.target as HTMLElement;

    if (elem.closest(dataDropMenuIgnore)) return;

    if (disable) return;

    changeIsOpen((prev) => {
      const next = !prev;

      onChangeState?.(next);

      return next;
    });
  }

  const renderMenu = useMemo(
    () =>
      menu.map(
        ({
          title,
          content,
          isActive,
          className,
          key,
          divider,
          icon,
          color = 'primary',
          disable: itemDisabled = false,
        }) => (
          <li
            key={key}
            onClick={
              !itemDisabled
                ? () => {
                    onClick(key);

                    changeIsOpen(false);

                    onChangeState?.(false);
                  }
                : undefined
            }
            className={dropListItemClassName}
          >
            <div
              className={
                className ||
                dropItemWrapperClassName(
                  isActive || activeKey === key,
                  MENU_COLORS[color],
                  color !== 'danger',
                  itemDisabled,
                )
              }
            >
              {icon && (
                <div className="mr-[8px] shrink-0">
                  <Icon size={14} name={icon} className={MENU_COLORS[color]} />
                </div>
              )}

              {content || title}
            </div>

            {divider && <div className="w-full h-[0.5px] bg-2color my-1" />}
          </li>
        ),
      ),
    [menu, activeKey, onClick, onChangeState],
  );

  const renderTriangle = useMemo(
    () => (
      <div
        className={dropTriangleClassName(DROP_DOWN_POSITION[position], dropTop)}
        style={{
          paddingLeft: `${dropOffset}px`,
          paddingRight: `${dropOffset}px`,
        }}
      >
        <TriangleBottomIcon
          size={13}
          className={cn(dropTop ? 'rotate-90 mt-[-10px]' : '-rotate-90', 'text-white shadow-lg')}
        />
      </div>
    ),
    [dropOffset, position, dropTop],
  );

  return (
    <div
      ref={rootRef}
      onPointerDown={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      className="relative inline-flex items-center justify-center"
    >
      <div className="select-none">
        <div
          ref={dropWrapperRef}
          className={cn(
            'inline-flex',
            DROP_DOWN_POSITION[position],
            disable && 'pointer-events-none opacity-50',
          )}
          onClick={handleToggleDropMenu}
        >
          {isOpen ? (
            children
          ) : (
            <TooltipWrapper content={hintTitle} id={tooltipId}>
              {children}
            </TooltipWrapper>
          )}
        </div>

        {isOpen &&
          createPortal(
            <div
              ref={dropMenuRef}
              className={cn(
                dropContainerClassName({
                  isOpen: true,
                  dropTop,
                  vladSpecialProp,
                }),
                'fixed z-[9999]',
              )}
              style={{
                ...sizeStyle,
                top: menuPosition?.top,
                left: menuPosition.left,
                bottom: menuPosition?.bottom,
              }}
              onClick={(event) => event.stopPropagation()}
            >
              {!dropTop && renderTriangle}

              <div style={dropHeightStyle} className="overflow-auto">
                {renderMenu}
              </div>

              {dropTop && renderTriangle}
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
};

export default DropMenu;
